// The `stable` environment is the universal non-prod fallback: when the app
// stack is only partially running (a local partial boot, or a remote
// dev/branch/preview env that didn't deploy every service), any service the
// current context doesn't host itself resolves to that service's instance in
// the remote `stable` environment — never localhost (it isn't running locally),
// never the same env (it isn't deployed there). Prod never falls back to it.
//
// v2 services now run on Azure Container Apps in `stable`. `stable` is non-prod,
// so it runs in Single-revision mode: the plain app FQDN `<app>-stable.<stable-domain>`
// already points at the single live revision. Do NOT append a `---<label>` cohort
// FQDN here — those are prod-only (blue-green current/previous/next routing) and a
// `<app>-stable---current.<domain>` host 404s at the ingress (there is no such
// label revision in `stable`).
//
// `<stable-domain>` is the `stable` Container Apps env `default_domain`, which
// is dynamic (Azure-generated per env). It's injected at BUILD time via
// `process.env.STABLE_CONTAINERAPP_ENV_DOMAIN` (vite `define`, fed from the CI
// build env / tools/dev/dev.sh — see vite.config.ts). This is a cross-env
// reference, so there is no runtime substitution for it (unlike the current
// env's `__CONTAINERAPP_DOMAIN__`). When it's absent we degrade to an
// RFC-reserved `.invalid` sentinel host: the URL is syntactically valid (so
// module init doesn't crash a local build) but guaranteed dead and greppable —
// NEVER the retired `*.azurewebsites.net` App Service fleet.
//
// Mirrors tools/lib/utils.js `getFallbackUrl` (the CI-side resolver) so the
// bundle's build-time defaults and CI-generated ENV_CONFIG agree on "stable".

const STABLE_ENV = "stable"

// RFC 2606 reserves `.invalid` — this host can never resolve. Duplicated by
// name in tools/lib/utils.js (CJS can't import this TS module); keep in sync.
const UNRESOLVED_STABLE_DOMAIN = "stable-domain-unresolved.invalid"

// Must match getDeployName's cap in tools/lib/utils.js (Azure Container Apps
// resource-name limit). `<project>-stable` is well under this for every current
// v2 service, but truncate to stay correct if a longer name is added.
const DEPLOY_NAME_MAX_LENGTH = 32

// Injected by each frontend's vite.config.ts `define` block. Empty string
// when the build didn't resolve the stable env's domain (e.g. local build
// without it, or before `stable` exists on Container Apps).
const STABLE_CONTAINERAPP_ENV_DOMAIN =
  process.env.STABLE_CONTAINERAPP_ENV_DOMAIN || ""

function stableDeployName(projectName: string): string {
  return `${projectName}-${STABLE_ENV}`
    .slice(0, DEPLOY_NAME_MAX_LENGTH)
    .replace(/[^a-zA-Z0-9]+$/, "")
}

/**
 * Build the `stable`-environment fallback URL for a v2 service.
 *
 * @param projectName Nx project name, e.g. "v2-supergraph".
 * @param opts.ws     Use the `wss:` scheme (WebSocket) instead of `https:`.
 */
export function stableFallbackUrl(
  projectName: string,
  opts: { ws?: boolean } = {},
): string {
  const scheme = opts.ws ? "wss" : "https"
  const domain = STABLE_CONTAINERAPP_ENV_DOMAIN || UNRESOLVED_STABLE_DOMAIN
  return `${scheme}://${stableDeployName(projectName)}.${domain}`
}
